import { axiosGetV3 } from '../../api/axios-handler';
import { useQuery } from '@tanstack/react-query';
import { getSessionSeed } from '../../utils/sessionSeed';
import { fetchVimeoMetadataBatch, addVimeoMetadataToExperiences } from './useBrowseExperiences';
import { Content, Resource, People } from './useExperiences';

export interface CollectionItem {
  id: number;
  created_at: string;
  resource_id?: number;
  listener_role_id?: number;
  listener_audio_id?: number;
  chatbot_id?: number;
  data: Content | Resource | People | any; // chatbot data type TBD
  sort_order: number;
}

export interface ContentCollection {
  id: number;
  created_at: string;
  name: string;
  description: string;
  items: CollectionItem[];
  total_items_count: number;
  verticals: any[];
  tags: Tag[];
  visibility: boolean;
  client_id?: number;
  is_public?: boolean;
  public_key?: string;
}

export interface Tag {
  id: number;
  name: string;
  confidence_score: number;
  tag_type: string;
}

export const addVimeoMetadataToCollectionItems = async (items: CollectionItem[]): Promise<CollectionItem[]> => {
  const videoItems = items.filter(
    (item) => (item.data as Content)?.video_id && !(item.data as any)?.vimeo_thumbnail_url,
  );

  if (videoItems.length === 0) {
    return items;
  }

  const videoIds = Array.from(
    new Set(videoItems.map((item) => (item.data as Content).video_id).filter((id): id is string => Boolean(id))),
  );
  const metadataMap = await fetchVimeoMetadataBatch(videoIds);

  return items.map((item) => {
    const content = item.data as Content;
    if (content?.video_id) {
      return {
        ...item,
        data: addVimeoMetadataToExperiences(content, metadataMap),
      };
    }
    return item;
  });
};

const getContentCollection = async (collectionId: number): Promise<ContentCollection> => {
  const response = await axiosGetV3(`/content_collections/${collectionId}`, { seed: getSessionSeed() });
  const data: ContentCollection = response.data;

  data.items = await addVimeoMetadataToCollectionItems(data.items);

  return data;
};

export const useContentCollection = (collectionId?: number) => {
  return useQuery<ContentCollection>(['contentCollection', collectionId], () => getContentCollection(collectionId!), {
    enabled: !!collectionId,
  });
};

export interface LandingPageCollectionsResponse {
  topic_collections: ContentCollection[];
  vertical_collections: ContentCollection[];
}

/**
 * Checks session storage for a randomized order of topic collections and returns it if it exists.
 * If it doesn't, it creates a new randomized order and stores it in session storage.
 * @param collections - The collections to randomize
 * @returns The randomized collections
 */
const getOrCreateRandomizedOrder = (collections: ContentCollection[]): ContentCollection[] => {
  if (collections.length === 0) return collections;

  const SESSION_KEY = 'topic_collections_order';
  const currentIds = new Set(collections.map((c) => c.id));
  const stored = sessionStorage.getItem(SESSION_KEY);

  if (stored) {
    try {
      const storedIds = JSON.parse(stored) as number[];
      const storedSet = new Set(storedIds);

      // Collections match if sets are equal (same IDs, any order)
      if (storedSet.size === currentIds.size && [...storedSet].every((id) => currentIds.has(id))) {
        // Reorder in O(n) using Map lookup
        const collectionMap = new Map(collections.map((c) => [c.id, c]));
        return storedIds.map((id) => collectionMap.get(id)!);
      }
    } catch {
      // Invalid stored data, fall through
    }
  }

  const shuffled = [...collections];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  sessionStorage.setItem(SESSION_KEY, JSON.stringify(shuffled.map((c) => c.id)));
  return shuffled;
};

const getLandingPageCollections = async (): Promise<LandingPageCollectionsResponse> => {
  const response = await axiosGetV3('/content_collections/landing_page', { seed: getSessionSeed() });
  const data: LandingPageCollectionsResponse = response.data;

  // Randomize topic collections order per session
  if (data.topic_collections?.length) {
    data.topic_collections = getOrCreateRandomizedOrder(data.topic_collections);
  }

  // Add vimeo metadata to all collection items simultaneously to avoid duplicate API calls for each collection
  const allItems = [
    ...(data.topic_collections || []).flatMap((c) => c.items),
    ...(data.vertical_collections || []).flatMap((c) => c.items),
  ];
  const itemsWithMetadata = await addVimeoMetadataToCollectionItems(allItems);
  const itemsMap = new Map(itemsWithMetadata.map((item) => [item.id, item]));
  const updateCollection = (collection: ContentCollection): ContentCollection => ({
    ...collection,
    items: collection.items.map((item) => itemsMap.get(item.id) ?? item),
  });

  return {
    topic_collections: (data.topic_collections || []).map(updateCollection),
    vertical_collections: (data.vertical_collections || []).map(updateCollection),
  };
};

export const useLandingPageCollections = () => {
  return useQuery<LandingPageCollectionsResponse>(['landingPageCollections'], getLandingPageCollections);
};
